import { useAuth } from "@/contexts/AuthContext";
import { db } from "@/lib/firebase";
import { getFirebaseErrorMessage } from "@/lib/firebaseErrors";
import { Crop } from "@/types/crop";
import { EntryCollection } from "@/types/entryCollection";
import { EntryConfigUnion } from "@/types/entryConfig";
import { Experiment } from "@/types/experiment";
import { Location } from "@/types/location";
import { Organization } from "@/types/organization";
import { PendingInvite } from "@/types/pendingInvite";
import { Plot } from "@/types/plot";
import { Task } from "@/types/task";
import { Trial } from "@/types/trial";
import { User, UserPermission } from "@/types/user";
import { fromFirestoreDoc } from "@/lib/firestoreDoc";
import { timestampSortKey, type TimestampLike } from "@/lib/utils";
import { collection, doc, getDoc, onSnapshot } from "firebase/firestore";
import { useEffect, useMemo, useRef, useState } from "react";

// Custom hook to resolve experimentId if not provided
function useResolvedExperimentId(trialId: string | undefined, experimentId: string | undefined) {
  const { selectedOrganization } = useAuth();
  const [resolvedExperimentId, setResolvedExperimentId] = useState<string | undefined>(experimentId);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!trialId || !selectedOrganization?.id) {
      setResolvedExperimentId(undefined);
      setLoading(false);
      setError(null);
      return;
    }

    if (experimentId) {
      setResolvedExperimentId(experimentId);
      setLoading(false);
      setError(null);
      return;
    }

    // If no experimentId provided, fetch the first experiment for this trial
    setLoading(true);
    const experimentsRef = collection(db, `organizations/${selectedOrganization.id}/trials/${trialId}/experiments`);

    const unsubscribe = onSnapshot(
      experimentsRef,
      (snapshot) => {
        if (!snapshot.empty) {
          // Use the first experiment
          setResolvedExperimentId(snapshot.docs[0].id);
        } else {
          setResolvedExperimentId(undefined);
        }
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error("Error listening to experiments:", err);
        setError(new Error(getFirebaseErrorMessage(err, "load experiments")));
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [trialId, experimentId, selectedOrganization?.id]);

  return { resolvedExperimentId, loading, error };
}


/**
 * Orders documents by `createdAt`, earliest first.
 *
 * `timestampSortKey`, not `?.toMillis()`: the optional chain guards a missing
 * `createdAt` but still throws on the plain `{seconds,nanoseconds}` map that
 * mobile and admin-SDK writes produce. This runs BEFORE render, so a throw here
 * blanks the whole list rather than one row — and a non-finite value would
 * silently corrupt the order of the valid rows around it.
 */
const byCreatedAtAsc = (a: { createdAt?: TimestampLike }, b: { createdAt?: TimestampLike }) =>
  timestampSortKey(a.createdAt) - timestampSortKey(b.createdAt);

interface ContextBoundSnapshot<T> {
  contextKey: string | null;
  value: T;
  loading: boolean;
  error: Error | null;
}

const firestoreContextKey = (...parts: Array<string | undefined>): string | null =>
  parts.every((part): part is string => !!part) ? JSON.stringify(parts) : null;

export interface PendingInviteWithId extends PendingInvite {
  id: string; // The document ID (lowercased email)
}

export function useOrganization() {
  const { user, loading: authLoading, selectedOrganization, userRole } = useAuth();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [users, setUsers] = useState<UserPermission[]>([]);
  const [pendingInvites, setPendingInvites] = useState<PendingInviteWithId[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // Wait for auth to be ready and organization to be selected
    if (authLoading || !user || !selectedOrganization?.id) {
      setLoading(authLoading);
      return;
    }

    const orgId = selectedOrganization.id;
    const orgRef = doc(db, `organizations/${orgId}`);
    const permissionsRef = collection(db, `organizations/${orgId}/userPermissions`);
    const pendingInvitesRef = collection(db, `organizations/${orgId}/pendingInvites`);

    // Subscribe to organization changes
    const unsubscribeOrg = onSnapshot(
      orgRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const orgData = snapshot.data();
          const org = {
            id: snapshot.id,
            ...orgData,
          } as Organization;
          setOrganization(org);
        } else {
          setOrganization(null);
        }
      },
      (err) => {
        console.error("Error listening to organization:", err);
        setError(new Error(getFirebaseErrorMessage(err, "load organization")));
      }
    );

    // Subscribe to user permissions changes
    const unsubscribePermissions = onSnapshot(
      permissionsRef,
      (snapshot) => {
        try {
          // Read profile data directly from userPermissions (no async getDoc needed)
          const usersData = snapshot.docs
            .map((permissionDoc) => {
              const permissionData = permissionDoc.data();
              const userId = permissionDoc.id;

              // Check if permission has profile data (required after migration)
              if (!permissionData.email || !permissionData.firstName || !permissionData.lastName) {
                console.warn(`UserPermission ${userId} missing profile data - may need migration`);
                return null;
              }

              return {
                id: userId,
                ...permissionData,
              } as UserPermission;
            })
            .filter((user): user is UserPermission => user !== null)
            .sort((a, b) => {
              // Active users come before disabled users
              if (a.status === "active" && b.status === "disabled") return -1;
              if (a.status === "disabled" && b.status === "active") return 1;
              // If same status, sort by first name
              return a.firstName.localeCompare(b.firstName);
            });

          setUsers(usersData);
          setLoading(false);
        } catch (err) {
          console.error("Error processing permissions:", err);
          setError(new Error(getFirebaseErrorMessage(err, "load team members")));
          setLoading(false);
        }
      },
      (err) => {
        console.error("Error listening to permissions:", err);
        setError(new Error(getFirebaseErrorMessage(err, "load team members")));
        setLoading(false);
      }
    );

    // Subscribe to pending invites (only admins can read these per security rules)
    let unsubscribePendingInvites: (() => void) | undefined;
    if (userRole === "admin") {
      unsubscribePendingInvites = onSnapshot(
        pendingInvitesRef,
        (snapshot) => {
          const invites = snapshot.docs
            .map((inviteDoc) => fromFirestoreDoc<PendingInviteWithId>(inviteDoc))
            .sort((a, b) => a.firstName.localeCompare(b.firstName));
          setPendingInvites(invites);
        },
        (err) => {
          console.error("Error listening to pending invites:", err);
          // Don't set error state for pending invites - non-admins will get permission denied
        }
      );
    } else {
      setPendingInvites([]);
    }

    return () => {
      unsubscribeOrg();
      unsubscribePermissions();
      unsubscribePendingInvites?.();
    };
  }, [authLoading, user, selectedOrganization?.id, userRole]);

  return { organization, users, pendingInvites, loading, error };
}

// Helper function to fetch experiments for a trial
const useExperimentsForTrial = (trialId: string | undefined) => useExperiments(trialId);

const EMPTY_TRIALS: Trial[] = [];

export function useTrials() {
  const {
    selectedOrganization,
    loading: authLoading,
    isAuthenticated,
    organizations,
    isSuperAdmin,
    allOrganizations,
  } = useAuth();
  const [trialsSnapshot, setTrialsSnapshot] = useState<ContextBoundSnapshot<Trial[]>>({
    contextKey: null,
    value: EMPTY_TRIALS,
    loading: true,
    error: null,
  });
  const orgId = selectedOrganization?.id;
  const requestedContextKey = firestoreContextKey(orgId);

  useEffect(() => {
    if (!orgId) {
      setTrialsSnapshot({ contextKey: null, value: EMPTY_TRIALS, loading: false, error: null });
      return;
    }

    const trialsRef = collection(db, `organizations/${orgId}/trials`);

    const unsubscribe = onSnapshot(
      trialsRef,
      (snapshot) => {
        const newTrials = snapshot.docs.map((doc) => fromFirestoreDoc<Trial>(doc));
        // Sort by createdAt ascending (earliest first)
        const sortedTrials = newTrials.sort(byCreatedAtAsc);
        setTrialsSnapshot({
          contextKey: requestedContextKey,
          value: sortedTrials,
          loading: false,
          error: null,
        });
      },
      (err) => {
        console.error("Error listening to trials:", err);
        setTrialsSnapshot({
          contextKey: requestedContextKey,
          value: EMPTY_TRIALS,
          loading: false,
          error: new Error(getFirebaseErrorMessage(err, "load trials")),
        });
      }
    );

    return () => unsubscribe();
  }, [orgId, requestedContextKey]);

  // State is retained across listener replacement, but the context key prevents
  // organization A's rows from ever rendering under organization B's identity.
  const isCurrentTrialsSnapshot =
    requestedContextKey !== null && trialsSnapshot.contextKey === requestedContextKey;
  const trials = isCurrentTrialsSnapshot ? trialsSnapshot.value : EMPTY_TRIALS;
  const hasKnownAvailableOrganization =
    Boolean(organizations?.length) || Boolean(isSuperAdmin && allOrganizations?.length);
  const organizationSelectionPending =
    authLoading ||
    (isAuthenticated && !selectedOrganization && hasKnownAvailableOrganization);

  return {
    trials,
    loading:
      organizationSelectionPending ||
      (requestedContextKey !== null && (!isCurrentTrialsSnapshot || trialsSnapshot.loading)),
    error: isCurrentTrialsSnapshot ? trialsSnapshot.error : null,
  };
}

export function useTrial(trialId: string | undefined) {
  const { selectedOrganization } = useAuth();
  const [snapshot, setSnapshot] = useState<ContextBoundSnapshot<Trial | null>>({
    contextKey: null,
    value: null,
    loading: true,
    error: null,
  });
  const { experiments } = useExperimentsForTrial(trialId);
  const requestedContextKey = firestoreContextKey(selectedOrganization?.id, trialId);

  useEffect(() => {
    if (!trialId || !selectedOrganization?.id) {
      setSnapshot({ contextKey: null, value: null, loading: false, error: null });
      return;
    }

    const trialRef = doc(db, `organizations/${selectedOrganization.id}/trials/${trialId}`);

    const unsubscribe = onSnapshot(
      trialRef,
      (snapshot) => {
        setSnapshot({
          contextKey: requestedContextKey,
          value: snapshot.exists() ? fromFirestoreDoc<Trial>(snapshot) : null,
          loading: false,
          error: null,
        });
      },
      (err) => {
        console.error("Error listening to trial:", err);
        setSnapshot({
          contextKey: requestedContextKey,
          value: null,
          loading: false,
          error: new Error(getFirebaseErrorMessage(err, "load trial")),
        });
      }
    );

    return () => unsubscribe();
  }, [requestedContextKey, trialId, selectedOrganization?.id]);

  const isCurrentSnapshot = requestedContextKey !== null && snapshot.contextKey === requestedContextKey;
  return {
    trial: isCurrentSnapshot ? snapshot.value : null,
    experiments: isCurrentSnapshot ? experiments : [],
    loading: requestedContextKey !== null && (!isCurrentSnapshot || snapshot.loading),
    error: isCurrentSnapshot ? snapshot.error : null,
  };
}

const EMPTY_EXPERIMENTS: Experiment[] = [];

export function useExperiments(trialId: string | undefined) {
  const { selectedOrganization } = useAuth();
  const [snapshot, setSnapshot] = useState<ContextBoundSnapshot<Experiment[]>>({
    contextKey: null,
    value: EMPTY_EXPERIMENTS,
    loading: true,
    error: null,
  });
  const requestedContextKey = firestoreContextKey(selectedOrganization?.id, trialId);

  useEffect(() => {
    if (!trialId || !selectedOrganization?.id) {
      setSnapshot({ contextKey: null, value: EMPTY_EXPERIMENTS, loading: false, error: null });
      return;
    }

    const experimentsRef = collection(db, `organizations/${selectedOrganization.id}/trials/${trialId}/experiments`);

    const unsubscribe = onSnapshot(
      experimentsRef,
      (snapshot) => {
        const newExperiments = snapshot.docs.map((doc) => fromFirestoreDoc<Experiment>(doc));
        // Sort by createdAt ascending (earliest first)
        const sortedExperiments = newExperiments.sort(byCreatedAtAsc);
        setSnapshot({
          contextKey: requestedContextKey,
          value: sortedExperiments,
          loading: false,
          error: null,
        });
      },
      (err) => {
        console.error("Error listening to experiments:", err);
        setSnapshot({
          contextKey: requestedContextKey,
          value: EMPTY_EXPERIMENTS,
          loading: false,
          error: new Error(getFirebaseErrorMessage(err, "load experiments")),
        });
      }
    );

    return () => unsubscribe();
  }, [requestedContextKey, trialId, selectedOrganization?.id]);

  const isCurrentSnapshot = requestedContextKey !== null && snapshot.contextKey === requestedContextKey;
  return {
    experiments: isCurrentSnapshot ? snapshot.value : EMPTY_EXPERIMENTS,
    loading: requestedContextKey !== null && (!isCurrentSnapshot || snapshot.loading),
    error: isCurrentSnapshot ? snapshot.error : null,
  };
}

export function useExperiment(trialId: string | undefined, experimentId: string | undefined) {
  const { selectedOrganization } = useAuth();
  const [snapshot, setSnapshot] = useState<ContextBoundSnapshot<Experiment | null>>({
    contextKey: null,
    value: null,
    loading: true,
    error: null,
  });
  const {
    resolvedExperimentId,
    loading: experimentLoading,
    error: experimentError,
  } = useResolvedExperimentId(trialId, experimentId);
  const effectiveExperimentId = experimentId ?? resolvedExperimentId;
  const requestedContextKey = firestoreContextKey(selectedOrganization?.id, trialId, effectiveExperimentId);

  useEffect(() => {
    if (!trialId || !selectedOrganization?.id) {
      setSnapshot({ contextKey: null, value: null, loading: false, error: null });
      return;
    }

    // If we have a trialId but no resolved experimentId, we're still loading
    if (!effectiveExperimentId) {
      setSnapshot({ contextKey: null, value: null, loading: true, error: null });
      return;
    }

    const experimentRef = doc(db, `organizations/${selectedOrganization.id}/trials/${trialId}/experiments/${effectiveExperimentId}`);

    const unsubscribe = onSnapshot(
      experimentRef,
      (snapshot) => {
        setSnapshot({
          contextKey: requestedContextKey,
          value: snapshot.exists() ? fromFirestoreDoc<Experiment>(snapshot) : null,
          loading: false,
          error: null,
        });
      },
      (err) => {
        console.error("Error listening to experiment:", err);
        setSnapshot({
          contextKey: requestedContextKey,
          value: null,
          loading: false,
          error: new Error(getFirebaseErrorMessage(err, "load experiment")),
        });
      }
    );

    return () => unsubscribe();
  }, [trialId, effectiveExperimentId, selectedOrganization?.id, requestedContextKey]);

  const isCurrentSnapshot = requestedContextKey !== null && snapshot.contextKey === requestedContextKey;
  const waitingForContext = !!trialId && !!selectedOrganization?.id && !effectiveExperimentId;

  return {
    experiment: isCurrentSnapshot ? snapshot.value : null,
    loading:
      experimentLoading ||
      waitingForContext ||
      (requestedContextKey !== null && (!isCurrentSnapshot || snapshot.loading)),
    error: experimentError || (isCurrentSnapshot ? snapshot.error : null),
  };
}

const EMPTY_PLOTS: Plot[] = [];

export interface PlotsSnapshotResult {
  plots: Plot[];
  loading: boolean;
  error: Error | null;
  requestedContextKey: string | null;
  deliveredContextKey: string | null;
  isCurrentSnapshot: boolean;
}

export function usePlots(trialId: string | undefined, experimentId: string | undefined) {
  const { selectedOrganization } = useAuth();
  const [snapshot, setSnapshot] = useState<ContextBoundSnapshot<Plot[]>>({
    contextKey: null,
    value: EMPTY_PLOTS,
    loading: true,
    error: null,
  });
  const {
    resolvedExperimentId,
    loading: experimentLoading,
    error: experimentError,
  } = useResolvedExperimentId(trialId, experimentId);
  const effectiveExperimentId = experimentId ?? resolvedExperimentId;
  const requestedContextKey = firestoreContextKey(selectedOrganization?.id, trialId, effectiveExperimentId);

  useEffect(() => {
    if (!trialId || !selectedOrganization?.id) {
      setSnapshot({ contextKey: null, value: EMPTY_PLOTS, loading: false, error: null });
      return;
    }

    // If we have a trialId but no resolved experimentId, we're still loading
    if (!effectiveExperimentId) {
      setSnapshot({ contextKey: null, value: EMPTY_PLOTS, loading: true, error: null });
      return;
    }

    const plotsRef = collection(
      db,
      `organizations/${selectedOrganization.id}/trials/${trialId}/experiments/${effectiveExperimentId}/plots`
    );

    const unsubscribe = onSnapshot(
      plotsRef,
      (snapshot) => {
        const newPlots = snapshot.docs.map((doc) => fromFirestoreDoc<Plot>(doc, "plotID"));
        setSnapshot({
          contextKey: requestedContextKey,
          value: newPlots,
          loading: false,
          error: null,
        });
      },
      (err) => {
        console.error("Error listening to plots:", err);
        setSnapshot({
          contextKey: requestedContextKey,
          value: EMPTY_PLOTS,
          loading: false,
          error: new Error(getFirebaseErrorMessage(err, "load plots")),
        });
      }
    );

    return () => unsubscribe();
  }, [trialId, effectiveExperimentId, selectedOrganization?.id, requestedContextKey]);

  const isCurrentSnapshot = requestedContextKey !== null && snapshot.contextKey === requestedContextKey;
  const waitingForContext = !!trialId && !!selectedOrganization?.id && !effectiveExperimentId;

  return {
    plots: isCurrentSnapshot ? snapshot.value : EMPTY_PLOTS,
    loading:
      experimentLoading ||
      waitingForContext ||
      (requestedContextKey !== null && (!isCurrentSnapshot || snapshot.loading)),
    error: experimentError || (isCurrentSnapshot ? snapshot.error : null),
    requestedContextKey,
    deliveredContextKey: snapshot.contextKey,
    isCurrentSnapshot,
  } satisfies PlotsSnapshotResult;
}

export function usePlot(trialId: string | undefined, experimentId: string | undefined, plotId: string | undefined) {
  const { selectedOrganization } = useAuth();
  const [snapshot, setSnapshot] = useState<ContextBoundSnapshot<Plot | null>>({
    contextKey: null,
    value: null,
    loading: true,
    error: null,
  });
  const {
    resolvedExperimentId,
    loading: experimentLoading,
    error: experimentError,
  } = useResolvedExperimentId(trialId, experimentId);
  const effectiveExperimentId = experimentId ?? resolvedExperimentId;
  const requestedContextKey = firestoreContextKey(
    selectedOrganization?.id,
    trialId,
    effectiveExperimentId,
    plotId
  );

  useEffect(() => {
    if (!trialId || !selectedOrganization?.id) {
      setSnapshot({ contextKey: null, value: null, loading: false, error: null });
      return;
    }

    // If we have a trialId but no resolved experimentId, we're still loading
    if (!effectiveExperimentId) {
      setSnapshot({ contextKey: null, value: null, loading: true, error: null });
      return;
    }

    // If we have resolved experimentId but no plotId, we're still loading
    if (!plotId) {
      setSnapshot({ contextKey: null, value: null, loading: true, error: null });
      return;
    }

    const plotRef = doc(
      db,
      `organizations/${selectedOrganization.id}/trials/${trialId}/experiments/${effectiveExperimentId}/plots/${plotId}`
    );

    const unsubscribe = onSnapshot(
      plotRef,
      (snapshot) => {
        setSnapshot({
          contextKey: requestedContextKey,
          value: snapshot.exists() ? fromFirestoreDoc<Plot>(snapshot, "plotID") : null,
          loading: false,
          error: null,
        });
      },
      (err) => {
        console.error("Error listening to plot:", err);
        setSnapshot({
          contextKey: requestedContextKey,
          value: null,
          loading: false,
          error: new Error(getFirebaseErrorMessage(err, "load plot")),
        });
      }
    );

    return () => unsubscribe();
  }, [trialId, effectiveExperimentId, plotId, selectedOrganization?.id, requestedContextKey]);

  const isCurrentSnapshot = requestedContextKey !== null && snapshot.contextKey === requestedContextKey;
  const waitingForContext =
    !!trialId && !!selectedOrganization?.id && (!effectiveExperimentId || !plotId);

  return {
    plot: isCurrentSnapshot ? snapshot.value : null,
    loading:
      experimentLoading ||
      waitingForContext ||
      (requestedContextKey !== null && (!isCurrentSnapshot || snapshot.loading)),
    error: experimentError || (isCurrentSnapshot ? snapshot.error : null),
  };
}

const EMPTY_TASKS: Task[] = [];

export function useTasks(trialId: string | undefined, experimentId: string | undefined) {
  const { selectedOrganization } = useAuth();
  const [snapshot, setSnapshot] = useState<ContextBoundSnapshot<Task[]>>({
    contextKey: null,
    value: EMPTY_TASKS,
    loading: true,
    error: null,
  });
  const {
    resolvedExperimentId,
    loading: experimentLoading,
    error: experimentError,
  } = useResolvedExperimentId(trialId, experimentId);
  const effectiveExperimentId = experimentId ?? resolvedExperimentId;
  const requestedContextKey = firestoreContextKey(selectedOrganization?.id, trialId, effectiveExperimentId);

  useEffect(() => {
    if (!trialId || !selectedOrganization?.id) {
      setSnapshot({ contextKey: null, value: EMPTY_TASKS, loading: false, error: null });
      return;
    }

    // If we're still loading experiments, wait
    if (experimentLoading) {
      setSnapshot((current) => ({ ...current, loading: true }));
      return;
    }

    // If experiment loading is complete but no experiment found, show empty state
    if (!effectiveExperimentId) {
      setSnapshot({ contextKey: null, value: EMPTY_TASKS, loading: false, error: null });
      return;
    }

    const tasksRef = collection(
      db,
      `organizations/${selectedOrganization.id}/trials/${trialId}/experiments/${effectiveExperimentId}/tasks`
    );

    const unsubscribe = onSnapshot(
      tasksRef,
      (snapshot) => {
        const newTasks = snapshot.docs.map((doc) => fromFirestoreDoc<Task>(doc));
        setSnapshot({
          contextKey: requestedContextKey,
          value: newTasks,
          loading: false,
          error: null,
        });
      },
      (err) => {
        console.error("Error listening to tasks:", err);
        setSnapshot({
          contextKey: requestedContextKey,
          value: EMPTY_TASKS,
          loading: false,
          error: new Error(getFirebaseErrorMessage(err, "load tasks")),
        });
      }
    );

    return () => unsubscribe();
  }, [
    trialId,
    effectiveExperimentId,
    selectedOrganization?.id,
    experimentLoading,
    requestedContextKey,
  ]);

  const isCurrentSnapshot = requestedContextKey !== null && snapshot.contextKey === requestedContextKey;
  const waitingForContext = !!trialId && !!selectedOrganization?.id && !effectiveExperimentId && experimentLoading;

  return {
    tasks: isCurrentSnapshot ? snapshot.value : EMPTY_TASKS,
    loading:
      experimentLoading ||
      waitingForContext ||
      (requestedContextKey !== null && (!isCurrentSnapshot || snapshot.loading)),
    error: experimentError || (isCurrentSnapshot ? snapshot.error : null),
  };
}

export function useTask(trialId: string | undefined, experimentId: string | undefined, taskId: string | undefined) {
  const { selectedOrganization } = useAuth();
  const [snapshot, setSnapshot] = useState<ContextBoundSnapshot<Task | null>>({
    contextKey: null,
    value: null,
    loading: true,
    error: null,
  });
  const {
    resolvedExperimentId,
    loading: experimentLoading,
    error: experimentError,
  } = useResolvedExperimentId(trialId, experimentId);
  const effectiveExperimentId = experimentId ?? resolvedExperimentId;
  const requestedContextKey = firestoreContextKey(
    selectedOrganization?.id,
    trialId,
    effectiveExperimentId,
    taskId
  );

  useEffect(() => {
    if (!trialId || !selectedOrganization?.id) {
      setSnapshot({ contextKey: null, value: null, loading: false, error: null });
      return;
    }

    // If we have a trialId but no resolved experimentId, we're still loading
    if (!effectiveExperimentId || !taskId) {
      setSnapshot({ contextKey: null, value: null, loading: true, error: null });
      return;
    }

    const taskRef = doc(
      db,
      `organizations/${selectedOrganization.id}/trials/${trialId}/experiments/${effectiveExperimentId}/tasks/${taskId}`
    );

    const unsubscribe = onSnapshot(
      taskRef,
      (snapshot) => {
        setSnapshot({
          contextKey: requestedContextKey,
          value: snapshot.exists() ? fromFirestoreDoc<Task>(snapshot) : null,
          loading: false,
          error: null,
        });
      },
      (err) => {
        console.error("Error listening to task:", err);
        setSnapshot({
          contextKey: requestedContextKey,
          value: null,
          loading: false,
          error: new Error(getFirebaseErrorMessage(err, "load task")),
        });
      }
    );

    return () => unsubscribe();
  }, [trialId, effectiveExperimentId, taskId, selectedOrganization?.id, requestedContextKey]);

  const isCurrentSnapshot = requestedContextKey !== null && snapshot.contextKey === requestedContextKey;
  const waitingForContext =
    !!trialId && !!selectedOrganization?.id && (!effectiveExperimentId || !taskId);

  return {
    task: isCurrentSnapshot ? snapshot.value : null,
    loading:
      experimentLoading ||
      waitingForContext ||
      (requestedContextKey !== null && (!isCurrentSnapshot || snapshot.loading)),
    error: experimentError || (isCurrentSnapshot ? snapshot.error : null),
  };
}

// EntryCollection hooks
const EMPTY_ENTRY_COLLECTIONS: EntryCollection[] = [];

export interface EntryCollectionsSnapshotResult {
  entryCollections: EntryCollection[];
  loading: boolean;
  error: Error | null;
  requestedContextKey: string | null;
  deliveredContextKey: string | null;
  isCurrentSnapshot: boolean;
}

export function useEntryCollections(
  trialId: string | undefined,
  experimentId: string | undefined,
  taskId: string | undefined
): EntryCollectionsSnapshotResult {
  const { selectedOrganization } = useAuth();
  const [snapshot, setSnapshot] = useState<{
    contextKey: string | null;
    entryCollections: EntryCollection[];
    loading: boolean;
    error: Error | null;
  }>({
    contextKey: null,
    entryCollections: EMPTY_ENTRY_COLLECTIONS,
    loading: true,
    error: null,
  });
  const {
    resolvedExperimentId,
    loading: experimentLoading,
    error: experimentError,
  } = useResolvedExperimentId(trialId, experimentId);
  // An explicit route experiment id is authoritative immediately. The resolver
  // stores its value in state, so on A -> B navigation it otherwise retains A
  // for one render and can briefly subscribe to trial B / experiment A.
  const effectiveExperimentId = experimentId ?? resolvedExperimentId;
  const requestedContextKey = firestoreContextKey(
    selectedOrganization?.id,
    trialId,
    effectiveExperimentId,
    taskId
  );

  useEffect(() => {
    if (!trialId || !selectedOrganization?.id) {
      setSnapshot({
        contextKey: null,
        entryCollections: EMPTY_ENTRY_COLLECTIONS,
        loading: false,
        error: null,
      });
      return;
    }

    // If we have a trialId but no resolved experimentId, we're still loading
    if (!effectiveExperimentId) {
      setSnapshot({
        contextKey: null,
        entryCollections: EMPTY_ENTRY_COLLECTIONS,
        loading: true,
        error: null,
      });
      return;
    }

    // If we have resolved experimentId but no taskId, we're still loading
    if (!taskId) {
      setSnapshot({
        contextKey: null,
        entryCollections: EMPTY_ENTRY_COLLECTIONS,
        loading: true,
        error: null,
      });
      return;
    }

    const entryCollectionsRef = collection(
      db,
      `organizations/${selectedOrganization.id}/trials/${trialId}/experiments/${effectiveExperimentId}/tasks/${taskId}/entryCollections`
    );

    const unsubscribe = onSnapshot(
      entryCollectionsRef,
      (snapshot) => {
        const newEntryCollections = snapshot.docs.map((doc) => fromFirestoreDoc<EntryCollection>(doc));
        setSnapshot({
          contextKey: requestedContextKey,
          entryCollections: newEntryCollections,
          loading: false,
          error: null,
        });
      },
      (err) => {
        console.error("Error listening to entry collections:", err);
        setSnapshot({
          contextKey: requestedContextKey,
          entryCollections: EMPTY_ENTRY_COLLECTIONS,
          loading: false,
          error: new Error(getFirebaseErrorMessage(err, "load data entries")),
        });
      }
    );

    return () => unsubscribe();
  }, [trialId, effectiveExperimentId, taskId, selectedOrganization?.id, requestedContextKey]);

  const isCurrentSnapshot = requestedContextKey !== null && snapshot.contextKey === requestedContextKey;
  const waitingForContext =
    !!trialId && !!selectedOrganization?.id && (!effectiveExperimentId || !taskId);
  const isLoading =
    experimentLoading ||
    waitingForContext ||
    (requestedContextKey !== null && (!isCurrentSnapshot || snapshot.loading));
  const combinedError = experimentError || (isCurrentSnapshot ? snapshot.error : null);

  return {
    // Never expose A's rows under B's request identity. Consumers that only use
    // the legacy data/loading fields are safe by default, while the explicit
    // keys let mutation/export surfaces prove which listener delivered them.
    entryCollections: isCurrentSnapshot ? snapshot.entryCollections : EMPTY_ENTRY_COLLECTIONS,
    loading: isLoading,
    error: combinedError,
    requestedContextKey,
    deliveredContextKey: snapshot.contextKey,
    isCurrentSnapshot,
  };
}

export function useEntryCollection(
  trialId: string | undefined,
  experimentId: string | undefined,
  taskId: string | undefined,
  entryCollectionId: string | undefined
) {
  const { selectedOrganization } = useAuth();
  const [snapshot, setSnapshot] = useState<ContextBoundSnapshot<EntryCollection | null>>({
    contextKey: null,
    value: null,
    loading: true,
    error: null,
  });
  const {
    resolvedExperimentId,
    loading: experimentLoading,
    error: experimentError,
  } = useResolvedExperimentId(trialId, experimentId);
  const effectiveExperimentId = experimentId ?? resolvedExperimentId;
  const requestedContextKey = firestoreContextKey(
    selectedOrganization?.id,
    trialId,
    effectiveExperimentId,
    taskId,
    entryCollectionId
  );

  useEffect(() => {
    if (!trialId || !selectedOrganization?.id) {
      setSnapshot({ contextKey: null, value: null, loading: false, error: null });
      return;
    }

    // If we have a trialId but no resolved experimentId, we're still loading
    if (!effectiveExperimentId) {
      setSnapshot({ contextKey: null, value: null, loading: true, error: null });
      return;
    }

    // If we have resolved experimentId but no taskId, we're still loading
    if (!taskId) {
      setSnapshot({ contextKey: null, value: null, loading: true, error: null });
      return;
    }

    // If we have taskId but no entryCollectionId, we're still loading
    if (!entryCollectionId) {
      setSnapshot({ contextKey: null, value: null, loading: true, error: null });
      return;
    }

    const entryCollectionRef = doc(
      db,
      `organizations/${selectedOrganization.id}/trials/${trialId}/experiments/${effectiveExperimentId}/tasks/${taskId}/entryCollections/${entryCollectionId}`
    );

    const unsubscribe = onSnapshot(
      entryCollectionRef,
      (snapshot) => {
        setSnapshot({
          contextKey: requestedContextKey,
          value: snapshot.exists() ? fromFirestoreDoc<EntryCollection>(snapshot) : null,
          loading: false,
          error: null,
        });
      },
      (err) => {
        console.error("Error listening to entry collection:", err);
        setSnapshot({
          contextKey: requestedContextKey,
          value: null,
          loading: false,
          error: new Error(getFirebaseErrorMessage(err, "load data entry")),
        });
      }
    );

    return () => unsubscribe();
  }, [
    trialId,
    effectiveExperimentId,
    taskId,
    entryCollectionId,
    selectedOrganization?.id,
    requestedContextKey,
  ]);

  const isCurrentSnapshot = requestedContextKey !== null && snapshot.contextKey === requestedContextKey;
  const waitingForContext =
    !!trialId &&
    !!selectedOrganization?.id &&
    (!effectiveExperimentId || !taskId || !entryCollectionId);

  return {
    entryCollection: isCurrentSnapshot ? snapshot.value : null,
    loading:
      experimentLoading ||
      waitingForContext ||
      (requestedContextKey !== null && (!isCurrentSnapshot || snapshot.loading)),
    error: experimentError || (isCurrentSnapshot ? snapshot.error : null),
  };
}

// EntryConfig hooks
const EMPTY_ENTRY_CONFIGS: EntryConfigUnion[] = [];

export function useEntryConfigs() {
  const { selectedOrganization } = useAuth();
  const [snapshot, setSnapshot] = useState<ContextBoundSnapshot<EntryConfigUnion[]>>({
    contextKey: null,
    value: EMPTY_ENTRY_CONFIGS,
    loading: true,
    error: null,
  });
  const requestedContextKey = firestoreContextKey(selectedOrganization?.id);

  useEffect(() => {
    if (!selectedOrganization?.id) {
      setSnapshot({ contextKey: null, value: EMPTY_ENTRY_CONFIGS, loading: false, error: null });
      return;
    }

    const entryConfigsRef = collection(db, `organizations/${selectedOrganization.id}/entryConfigs`);

    const unsubscribe = onSnapshot(
      entryConfigsRef,
      (snapshot) => {
        const newEntryConfigs = snapshot.docs.map((doc) => fromFirestoreDoc<EntryConfigUnion>(doc));
        setSnapshot({
          contextKey: requestedContextKey,
          value: newEntryConfigs,
          loading: false,
          error: null,
        });
      },
      (err) => {
        console.error("Error listening to entry configs:", err);
        setSnapshot({
          contextKey: requestedContextKey,
          value: EMPTY_ENTRY_CONFIGS,
          loading: false,
          error: new Error(getFirebaseErrorMessage(err, "load traits")),
        });
      }
    );

    return () => unsubscribe();
  }, [requestedContextKey, selectedOrganization?.id]);

  const isCurrentSnapshot = requestedContextKey !== null && snapshot.contextKey === requestedContextKey;
  return {
    entryConfigs: isCurrentSnapshot ? snapshot.value : EMPTY_ENTRY_CONFIGS,
    loading: requestedContextKey !== null && (!isCurrentSnapshot || snapshot.loading),
    error: isCurrentSnapshot ? snapshot.error : null,
  };
}

export function useEntryConfig(entryConfigId: string | undefined) {
  const { selectedOrganization } = useAuth();
  const [entryConfig, setEntryConfig] = useState<EntryConfigUnion | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!selectedOrganization?.id) {
      setEntryConfig(null);
      setLoading(false);
      return;
    }

    if (!entryConfigId) {
      setEntryConfig(null);
      setLoading(false);
      return;
    }

    const entryConfigRef = doc(db, `organizations/${selectedOrganization.id}/entryConfigs/${entryConfigId}`);

    const unsubscribe = onSnapshot(
      entryConfigRef,
      (snapshot) => {
        if (snapshot.exists()) {
          setEntryConfig(fromFirestoreDoc<EntryConfigUnion>(snapshot));
        } else {
          setEntryConfig(null);
        }
        setLoading(false);
      },
      (err) => {
        console.error("Error listening to entry config:", err);
        setError(new Error(getFirebaseErrorMessage(err, "load trait")));
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [entryConfigId, selectedOrganization?.id]);

  return { entryConfig, loading, error };
}

/**
 * Custom hook that fetches EntryCollections for a task and cross-references them with plots
 * to provide plot data for visualization. Changes to EntryCollections are handled reactively.
 */
export function usePlotData(
  trialId: string | undefined,
  experimentId: string | undefined,
  taskId: string | undefined,
  plots: Plot[],
  entryCollectionsSnapshot: EntryCollectionsSnapshotResult
) {
  const {
    entryCollections,
    loading: entryCollectionsLoading,
    error: entryCollectionsError,
    isCurrentSnapshot,
  } = entryCollectionsSnapshot;
  const hasContext = !!trialId && !!taskId && (!!experimentId || entryCollectionsSnapshot.requestedContextKey !== null);
  const plotData = useMemo(
    () =>
      hasContext && isCurrentSnapshot
        ? plots.map((plot) => ({
            plot,
            entryCollections: entryCollections.filter(
              (entryCollection) => entryCollection.plotRef.id === plot.plotID
            ),
          }))
        : [],
    [entryCollections, hasContext, isCurrentSnapshot, plots]
  );

  return {
    plotData,
    loading: entryCollectionsLoading || (hasContext && !isCurrentSnapshot),
    error: entryCollectionsError,
  };
}

// Crops hooks
export function useCrops() {
  const { selectedOrganization } = useAuth();
  const [crops, setCrops] = useState<Crop[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Get the organization ID - this supports dynamic org switching for super admins
  const orgId = selectedOrganization?.id;

  // Track the previous orgId to detect actual changes vs effect re-runs
  const prevOrgIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!orgId) {
      setCrops([]);
      setLoading(false);
      return;
    }

    // Only clear state when the organization actually changes, not on effect re-runs
    // This prevents clearing crops during React Strict Mode double-invocation or HMR
    if (prevOrgIdRef.current !== orgId) {
      setCrops([]);
      setLoading(true);
      setError(null);
      prevOrgIdRef.current = orgId;
    }

    const cropsRef = collection(db, `organizations/${orgId}/crops`);

    const unsubscribe = onSnapshot(
      cropsRef,
      (snapshot) => {
        const newCrops = snapshot.docs.map((doc) => fromFirestoreDoc<Crop>(doc));
        setCrops(newCrops);
        setLoading(false);
      },
      (err) => {
        console.error("Error listening to crops:", err);
        setError(new Error(getFirebaseErrorMessage(err, "load crops")));
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [orgId]);

  return { crops, loading, error };
}

// Locations hooks
export function useLocations() {
  const { selectedOrganization } = useAuth();
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Get the organization ID - this supports dynamic org switching for super admins
  const orgId = selectedOrganization?.id;

  // Track the previous orgId to detect actual changes vs effect re-runs
  const prevOrgIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!orgId) {
      setLocations([]);
      setLoading(false);
      return;
    }

    // Only clear state when the organization actually changes, not on effect re-runs
    // This prevents clearing locations during React Strict Mode double-invocation or HMR
    if (prevOrgIdRef.current !== orgId) {
      setLocations([]);
      setLoading(true);
      setError(null);
      prevOrgIdRef.current = orgId;
    }

    const locationsRef = collection(db, `organizations/${orgId}/locations`);

    const unsubscribe = onSnapshot(
      locationsRef,
      (snapshot) => {
        const newLocations = snapshot.docs.map((doc) => fromFirestoreDoc<Location>(doc));
        setLocations(newLocations);
        setLoading(false);
      },
      (err) => {
        console.error("Error listening to locations:", err);
        setError(new Error(getFirebaseErrorMessage(err, "load locations")));
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [orgId]);

  return { locations, loading, error };
}
